/**
 * Which cells are selected, and where a keypress moves that.
 *
 * A cell is named by the row's id and the field's id, never by its position.
 * Positions move under the reader constantly -- somebody else inserts a row
 * above, the window scrolls and renumbers nothing but holds a different slice --
 * and a selection stored as "row 812" would quietly become a different row every
 * time one of those happened. Ids survive all of it, and the geometry is worked
 * out at the moment it is drawn.
 *
 * Pure: no store, no React, no DOM. What the overlay renders is a rectangle in
 * index space, and turning that into pixels is the component's business.
 */
import type { RowWindow } from '../rows/rowWindow'
import { indexOfRow, rowAt } from '../rows/rowWindow'
import type { GridColumn } from './columns'

export interface Cell {
  rowId: number
  fieldId: number
}

/**
 * A selected region: where it started and where it currently reaches.
 *
 * Anchor and head rather than top-left and bottom-right, because extending a
 * selection upwards is a real thing somebody does and the pair has to remember
 * which end is pinned. A single cell is the two being equal.
 */
export interface CellRange {
  anchor: Cell
  head: Cell
}

export const singleCell = (cell: Cell): CellRange => ({ anchor: cell, head: cell })

export type Direction = 'up' | 'down' | 'left' | 'right'

/** Where a cell sits, in rows of the view and columns of this view's order. */
export interface CellIndexes {
  row: number
  column: number
}

/** A rectangle in index space, inclusive of both corners. */
export interface SelectionRect {
  top: number
  left: number
  rows: number
  columns: number
}

/**
 * Where a cell is, or null when it is not on screen to be pointed at.
 *
 * A row outside the window and a field the view hides are the same answer: there
 * is nothing to draw. The selection itself survives either -- scrolling back
 * brings it into view again, and unhiding a column brings it back.
 */
export function cellIndexes(
  cell: Cell,
  window: RowWindow,
  columns: readonly GridColumn[],
): CellIndexes | null {
  const row = indexOfRow(window, cell.rowId)
  if (row === -1) return null

  const column = columns.findIndex((c) => c.field.id === cell.fieldId)
  if (column === -1) return null

  return { row, column }
}

/**
 * The rectangle covering a range, or null when neither corner can be placed.
 *
 * Clamped to the window rather than dropped when only one corner is off screen:
 * a selection dragged past the top of the viewport is still a selection, and the
 * part of it that is visible is the part worth drawing.
 */
export function rectFor(
  range: CellRange,
  window: RowWindow,
  columns: readonly GridColumn[],
): SelectionRect | null {
  const anchor = cellIndexes(range.anchor, window, columns)
  const head = cellIndexes(range.head, window, columns)
  if (anchor === null && head === null) return null

  const rows = [anchor?.row, head?.row].filter((v): v is number => v !== undefined)
  const cols = [anchor?.column, head?.column].filter((v): v is number => v !== undefined)

  const top = Math.min(...rows)
  const left = Math.min(...cols)
  return {
    top,
    left,
    rows: Math.max(...rows) - top + 1,
    columns: Math.max(...cols) - left + 1,
  }
}

const DELTA: Record<Direction, CellIndexes> = {
  up: { row: -1, column: 0 },
  down: { row: 1, column: 0 },
  left: { row: 0, column: -1 },
  right: { row: 0, column: 1 },
}

/**
 * Where an arrow key moves the selection.
 *
 * The head moves and, unless the move is extending a range, the anchor follows
 * it. Bounded by the rows the window is holding rather than by the view's total:
 * a cell can only be named by the id of a row that has been fetched, so the edge
 * of the buffer is the edge of what a keypress can reach. In practice that is
 * never felt -- the window holds a viewport and a request block either side, and
 * the grid scrolls the selection into view, which pulls the next block in.
 *
 * Returns the range unchanged at the edges rather than null, so a held arrow key
 * at the bottom of a table does nothing instead of clearing the selection.
 */
export function moveSelection(
  range: CellRange,
  direction: Direction,
  window: RowWindow,
  columns: readonly GridColumn[],
  { extend = false }: { extend?: boolean } = {},
): CellRange {
  const from = cellIndexes(range.head, window, columns)
  if (from === null) return range

  const delta = DELTA[direction]
  // Not clamped, because the lookups already answer the question. A row index
  // past either end of the buffer has no row at it and a column index past
  // either end has no column, and both mean the same thing: there is nowhere to
  // move to, so the selection stays where it is. Clamping first and then
  // checking is two guards for one edge, and the mutation pass will not tell
  // them apart.
  const targetRow = rowAt(window, from.row + delta.row)
  const targetColumn = columns[from.column + delta.column]
  if (targetRow === undefined || targetColumn === undefined) return range

  const head: Cell = { rowId: targetRow.id, fieldId: targetColumn.field.id }
  return extend ? { anchor: range.anchor, head } : singleCell(head)
}

/**
 * The cell at a pair of indexes, or null when there is nothing there.
 *
 * Both lookups can fail on a grid being refetched underneath the pointer, which
 * is why this answers null rather than assuming.
 */
export function cellAtIndexes(
  window: RowWindow,
  columns: readonly GridColumn[],
  indexes: CellIndexes,
): Cell | null {
  const row = rowAt(window, indexes.row)
  const column = columns[indexes.column]
  if (row === undefined || column === undefined) return null
  return { rowId: row.id, fieldId: column.field.id }
}

/**
 * Whether a cell is inside a selected region.
 *
 * Asked by the right-click: pressing the secondary button inside a selection
 * has to leave it alone, and outside it has to move it -- which is what every
 * file manager and every spreadsheet does, and what makes "delete these five
 * rows" reachable at all.
 */
export function rangeContains(
  range: CellRange,
  cell: Cell,
  window: RowWindow,
  columns: readonly GridColumn[],
): boolean {
  const rect = rectFor(range, window, columns)
  const at = cellIndexes(cell, window, columns)
  if (rect === null || at === null) return false

  return (
    at.row >= rect.top &&
    at.row < rect.top + rect.rows &&
    at.column >= rect.left &&
    at.column < rect.left + rect.columns
  )
}
